import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Chip,
  Select,
  Avatar,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { useLocation } from 'react-router-dom';

import Map from '../Map';
import useClasses from '../../../hooks/legacy/useClasses';
import CheckoutLayoutItem from '../CheckoutLayoutItem';
import { DELIVERY, DELIVERY_OPTIONS } from '../../../util/Constants';
import { useWhere } from '../../../hooks/useWhere';

import styles from './OrderAddress.style';

function OrderAddress({ onAdjustedValue, onCheckoutUpdating, onAddressValue }) {
  const classes = useClasses(styles);
  const [instructions, setInstructions] = useState('');
  const [doorCode, setDoorCode] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [suiteName, setSuiteName] = useState('');
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState(null);
  const location = useLocation();
  const isCheckoutPage = location.pathname.includes('checkout');
  const { dAddress } = useWhere();

  const {
    address,
    orderType,
    restaurantDetails,
    deliveryOptions,
    notes,
    deliveryOptionsInstruction,
  } = useSelector(({ order, restaurant, address: selectedAddress }) => ({
    address: selectedAddress.selectedAddress,
    orderType: order?.type,
    deliveryOptions: order.deliveryOptions,
    restaurantDetails: restaurant?.selectedRestaurantData,
    notes: order.notes,
    deliveryOptionsInstruction: order?.deliveryOptionsInstruction,
  }));

  const [homeOrTakeoutAddress, setHomeOrTakeoutAddress] = useState({});

  const isDelivery = orderType?.label === DELIVERY;
  const restaurantInfo = restaurantDetails?.response;
  const trackingCustomerAddressObj = dAddress || address;
  const trackingCustomerAddress = `${trackingCustomerAddressObj?.address}, ${
    trackingCustomerAddressObj?.city
  }, ${trackingCustomerAddressObj?.state}
  ${trackingCustomerAddressObj?.zip || trackingCustomerAddressObj?.zipcode}`;

  const subStringInstruction = deliveryOptionsInstruction?.replace?.(
    deliveryOptions?.label,
    ''
  );

  const onDeliveryInstructionChange = (e) => {
    setInstructions(e.target.value);
    if (onAddressValue) {
      onAddressValue('notes', e.target.value);
    }
  };

  const onDoorCodeChange = (e) => {
    setDoorCode(e.target.value);
    if (onAddressValue) {
      onAddressValue('doorcode', e.target.value);
    }
  };

  const onBusinessNameChange = (e) => {
    setBusinessName(e.target.value);
    if (onAddressValue) {
      onAddressValue('company', e.target.value);
    }
  };

  const onBuildingNameChange = (e) => {
    setBuildingName(e.target.value);
    if (onAddressValue) {
      onAddressValue('building', e.target.value);
    }
  };

  const onSuiteChange = (e) => {
    setSuiteName(e.target.value);
    if (onAddressValue) {
      onAddressValue('suite', e.target.value);
    }
  };

  useEffect(() => {
    if (
      deliveryOptionsInstruction &&
      deliveryOptionsInstruction.length > 0 &&
      instructions === null
    ) {
      setInstructions(deliveryOptionsInstruction);
    }
  }, [deliveryOptionsInstruction]);

  useEffect(() => {
    if (address) {
      if (address?.addition) {
        if (address.addition?.doorcode && doorCode === '') {
          setDoorCode(address.addition?.doorcode);
        }
        if (address.addition?.notes && instructions === '') {
          setInstructions(address.addition?.notes);
        }
        if (address.addition?.company && businessName === '') {
          setBusinessName(address.addition?.company);
        }
        if (address.addition?.building && buildingName === '') {
          setBuildingName(address.addition?.building);
        }
        if (address.addition?.suite && suiteName === '') {
          setSuiteName(address.addition?.suite);
        }
      }
    }
  }, [address]);

  useEffect(() => {
    if (restaurantInfo && address) {
      setHomeOrTakeoutAddress(
        !isDelivery
          ? {
              title: 'Store Address',
              subtitle: 'You will pick up your order here.',
              locationText: 'Pickup Location',
              profile: restaurantInfo?.id.toString(),
              titleIcon: <DirectionsWalkIcon />,
              name: restaurantInfo?.name,
              address: `${restaurantInfo?.address}, ${restaurantInfo?.city}, ${restaurantInfo?.state} ${restaurantInfo?.zipcode}`,
              location: {
                latitude: restaurantInfo?.latitude,
                longitude: restaurantInfo?.longitude,
              },
            }
          : {
              title: 'Delivery Address',
              subtitle: 'To change your address, please go back.',
              locationText: 'Your Delivery Address',
              profile: address?.id.toString(),
              titleIcon: <HomeIcon className={classes.iconBackground} />,
              address: address?.entered_address,
              location: {
                latitude: address?.latitude,
                longitude: address?.longitude,
              },
            }
      );
    }
  }, [restaurantInfo, address, isDelivery, classes.iconBackground]);

  const onDeliveryOptionChange = (evt) => {
    // dispatch(setDeliveryOptions(option));
    if (evt.target) {
      const options = DELIVERY_OPTIONS.filter(
        (v) => v.value === evt.target.value
      );

      if (options.length > 0) {
        const option = options[0];
        setSelectedDeliveryOption(option);
        if (onAdjustedValue) {
          onAdjustedValue('delivery_option', option.value);
        }
      }
    }
  };

  return (
    <CheckoutLayoutItem
      title={homeOrTakeoutAddress?.title}
      subtitle={homeOrTakeoutAddress?.subtitle}
    >
      <Stack spacing={3} direction="column">
        {Object.keys(homeOrTakeoutAddress).length > 0 && (
          <Box>
            <Box sx={{ width: '100%', height: '200px' }}>
              <Map list={[homeOrTakeoutAddress]} />
            </Box>
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                className={classes.locationMargin}
              >
                <Avatar className={classes.avatarColor}>
                  {homeOrTakeoutAddress?.titleIcon}
                </Avatar>
                <Typography fontWeight={600}>
                  {!isDelivery
                    ? homeOrTakeoutAddress?.locationText
                    : 'Your Delivery Address'}
                </Typography>
              </Stack>
              {homeOrTakeoutAddress.name && (
                <Typography
                  color="text.secondary"
                  className={classes.addressMargin}
                >
                  {homeOrTakeoutAddress.name}
                </Typography>
              )}
              {homeOrTakeoutAddress.address && (
                <Typography color="text.secondary">
                  {homeOrTakeoutAddress.address}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {isCheckoutPage && isDelivery && address && (
          <Stack spacing={2} direction="column">
            <Box>
              <Typography fontWeight={500}>Your Delivery Address</Typography>
              <Typography
                color="text.secondary"
                className={classes.addressText}
              >
                {trackingCustomerAddress}
              </Typography>
            </Box>
            <Box>Suite, Apartment, or Unit Number</Box>
            <TextField
              focused
              fullWidth
              name="suite"
              data-test-id="suite"
              placeholder="(Optional)"
              value={suiteName}
              onChange={onSuiteChange}
              disabled={onCheckoutUpdating}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Stack
                spacing={1}
                direction="column"
                className={classes.buildingAddressWidth}
              >
                <Box>Building Name</Box>
                <TextField
                  focused
                  fullWidth
                  name="building"
                  data-test-id="building"
                  placeholder="(Optional)"
                  value={buildingName}
                  onChange={onBuildingNameChange}
                  disabled={onCheckoutUpdating}
                />
              </Stack>
              <Stack
                spacing={1}
                direction="column"
                className={classes.buildingAddressWidth}
              >
                <Box>Business Name</Box>
                <TextField
                  focused
                  fullWidth
                  name="company"
                  data-test-id="company"
                  placeholder="(Optional)"
                  value={businessName}
                  onChange={onBusinessNameChange}
                  disabled={onCheckoutUpdating}
                />
              </Stack>
            </Stack>
            <Box>Door Code</Box>
            <TextField
              focused
              fullWidth
              name="doorcode"
              data-test-id="doorcode"
              placeholder="(Optional)"
              value={doorCode}
              onChange={onDoorCodeChange}
              disabled={onCheckoutUpdating}
            />
            <Box>Delivery Options</Box>
            <Select
              data-test-id="delivery-option-selector"
              value={
                selectedDeliveryOption !== null
                  ? selectedDeliveryOption.value
                  : 'meet_at_door'
              }
              onChange={onDeliveryOptionChange}
            >
              {DELIVERY_OPTIONS.map((option) => (
                <MenuItem value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
            <Box className={classes.messageColor}>
              <Stack
                direction="row"
                alignContent="center"
                justifyContent="center"
              >
                <Chip
                  size="small"
                  label="Important"
                  fullwidth="false"
                  sx={{
                    mr: 1,
                    color: 'white',
                    borderRadius: 4,
                    backgroundColor: 'orange',
                  }}
                />
                <Typography>Don’t forget to add notes.</Typography>
              </Stack>
            </Box>
            <Box className={classes.textFieldParent}>
              <Box mb={2}>Delivery Instructions</Box>
              <TextField
                focused
                rows={4}
                multiline
                fullWidth
                name="notes"
                value={instructions}
                data-test-id="deliveryInstructions"
                className={classes.innerTextFieldParent}
                onChange={onDeliveryInstructionChange}
                disabled={onCheckoutUpdating}
              />
              <Box className={classes.driverInstruction}>
                Provide your driver with dropoff instructions for your order.
              </Box>
            </Box>
          </Stack>
        )}

        {!isDelivery && (
          <Box>
            <Box>
              <Typography fontWeight={500}>
                {restaurantDetails?.name}
              </Typography>
              <Typography
                color="text.secondary"
                className={classes.addressText}
              >
                {`${restaurantDetails?.address}, ${restaurantDetails?.city}, ${restaurantDetails?.state} ${restaurantDetails?.zipcode}`}
              </Typography>
            </Box>
          </Box>
        )}

        {!isCheckoutPage && isDelivery && trackingCustomerAddressObj && (
          <Box>
            <Box>
              <Typography fontWeight={500}>Your Delivery Address</Typography>
              <Typography
                color="text.secondary"
                className={classes.addressText}
              >
                {`${
                  trackingCustomerAddressObj?.street ||
                  trackingCustomerAddressObj?.address
                }, ${trackingCustomerAddressObj?.city}, ${
                  trackingCustomerAddressObj?.state
                } ${
                  trackingCustomerAddressObj?.zip ||
                  trackingCustomerAddressObj?.zipcode
                }`}
              </Typography>
            </Box>
            {trackingCustomerAddressObj?.suite && (
              <Box>
                <Typography fontWeight={500}>
                  Suite, Apartment, or Unit Number
                </Typography>
                <Typography
                  color="text.secondary"
                  className={classes.addressText}
                >
                  {trackingCustomerAddressObj?.suite}
                </Typography>
              </Box>
            )}
            {trackingCustomerAddressObj?.building_name && (
              <Box>
                <Typography fontWeight={500}>Building Name</Typography>
                <Typography
                  color="text.secondary"
                  className={classes.addressText}
                >
                  {trackingCustomerAddressObj?.building_name}
                </Typography>
              </Box>
            )}
            {trackingCustomerAddressObj?.company && (
              <Box>
                <Typography fontWeight={500}>Business Name</Typography>
                <Typography
                  color="text.secondary"
                  className={classes.addressText}
                >
                  {trackingCustomerAddressObj?.company}
                </Typography>
              </Box>
            )}
            {trackingCustomerAddressObj?.door_code && (
              <Box>
                <Typography fontWeight={500}>Door Code</Typography>
                <Typography
                  color="text.secondary"
                  className={classes.addressText}
                >
                  {trackingCustomerAddressObj?.door_code}
                </Typography>
              </Box>
            )}
            {instructions && (
              <Box>
                <Typography fontWeight={500}>Delivery Notes</Typography>
                <Box>
                  <Box className={classes.parentNoteBox}>
                    {deliveryOptions?.image && (
                      <img
                        alt="no deliveryOption"
                        src={deliveryOptions?.image}
                        className={classes.optionImageMargin}
                      />
                    )}
                    <Typography color="text.secondary">
                      {deliveryOptions?.label}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    {subStringInstruction} {notes}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </CheckoutLayoutItem>
  );
}

OrderAddress.propTypes = {
  onAdjustedValue: PropTypes.func,
  onAddressValue: PropTypes.func,
  onCheckoutUpdating: PropTypes.bool.isRequired,
};

OrderAddress.defaultProps = {
  onAddressValue: null,
  onAdjustedValue: null,
};

export default OrderAddress;
